import { useEffect, useState } from 'react';
import { router } from '@inertiajs/react';
import { Plus, X } from 'lucide-react';
import TeacherShell from '../../../components/layout/TeacherShell';
import SentenceDisplay from '../../../components/sentence_studio/SentenceDisplay';
import ParseLayers from '../../../components/sentence_studio/ParseLayers';
import ContextWindow from '../../../components/sentence_studio/ContextWindow';
import WordLookupSelection from '../../../components/reader/WordLookupSelection';
import DynamicIsland from '../../../components/ui/DynamicIsland';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { truncateWords } from '../../../lib/truncateWords';

export default function SentenceStudioShow({ analysis, visit, from_chapter, ready, parse_error }) {
  const { push, replace } = useBreadcrumbs();
  const [lookupError, setLookupError] = useState(null);
  const [customText, setCustomText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [newSentenceOpen, setNewSentenceOpen] = useState(false);

  // Poll the server while the parse is still running in the background.
  // Inertia.router.reload re-fetches only the named props, so the page just
  // rerenders with the latest analysis without a full navigation.
  useEffect(() => {
    if (ready || parse_error) return;
    const id = setInterval(() => {
      router.reload({ only: ['analysis', 'visit', 'ready', 'parse_error'], preserveScroll: true });
    }, 1500);
    return () => clearInterval(id);
  }, [ready, parse_error]);

  // Reconstruct the full trail from server-provided context. Teachers may
  // land here from history (no source chapter), so we only include book/
  // chapter crumbs when there's a source chapter to anchor the hierarchy.
  useEffect(() => {
    const crumbs = [];
    if (from_chapter) {
      crumbs.push({
        level: 'book',
        label: from_chapter.book_title,
        href: `/teacher/library/${from_chapter.book_id}`,
      });
      crumbs.push({
        level: 'chapter',
        label: from_chapter.label || from_chapter.title,
        href: `/teacher/library/${from_chapter.book_id}?chapter=${from_chapter.id}`,
      });
    }
    crumbs.push({
      level: 'sentence',
      label: truncateWords(analysis.text, 40),
      href: `/teacher/sentence_studio/${analysis.id}${from_chapter ? `?from_chapter=${from_chapter.id}` : ''}`,
    });
    replace(crumbs);
  }, [analysis.id, from_chapter?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function onWordClick(token) {
    setLookupError(null);
    const q = token.lemma || token.text;
    try {
      const res = await fetch(`/teacher/dictionary/resolve?q=${encodeURIComponent(q)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.id) {
          push({
            level: 'word',
            label: data.lemma || q,
            href: `/teacher/dictionary/${data.id}`,
          });
          router.visit(`/teacher/dictionary/${data.id}`);
          return;
        }
      }
      setLookupError(`No dictionary entry for "${q}"`);
    } catch {
      setLookupError('Lookup failed — network error');
    }
  }

  function submitCustom(e) {
    e.preventDefault();
    if (!customText.trim() || submitting) return;
    setSubmitting(true);
    router.post(
      '/teacher/sentence_studio',
      { text: customText.trim() },
      { onFinish: () => setSubmitting(false) }
    );
  }

  return (
    <TeacherShell scrollTitle={truncateWords(analysis.text, 40)}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Sentence header: sentence on the left (clamped to 2 lines),
            New Sentence action on the right. */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1 line-clamp-2">
            <SentenceDisplay analysis={analysis} onWordClick={onWordClick} />
          </div>
          <button
            type="button"
            onClick={() => setNewSentenceOpen(true)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
            New Sentence
          </button>
        </div>
        {lookupError && (
          <p className="-mt-3 text-sm text-rose-500">{lookupError}</p>
        )}

        {/* Sentence Analysis frame — shows a loading placeholder until the
            background parse job finishes populating analysis.parse_data. */}
        {ready ? (
          <ParseLayers analysis={analysis} />
        ) : parse_error ? (
          <div className="rounded-xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-900/20 p-4 text-sm text-rose-700 dark:text-rose-300">
            Couldn&rsquo;t analyze this sentence: {parse_error}
          </div>
        ) : (
          <AnalysisPlaceholder />
        )}

        {/* Context frame — below the analysis. Word selection inside it
            navigates to the dictionary, mirroring the chapter reader. */}
        <WordLookupSelection>
          <ContextWindow
            analysisText={analysis.text}
            contextBefore={visit?.context_before}
            contextAfter={visit?.context_after}
            sourceChapter={from_chapter}
          />
        </WordLookupSelection>
      </div>

      <DynamicIsland
        isActive={!ready && !parse_error}
        steps={[
          'Reading sentence…',
          'Tagging parts of speech…',
          'Identifying phrases…',
          'Tracing clause structure…',
          'Finalizing analysis…',
        ]}
        stepDuration={1800}
      />

      {newSentenceOpen && (
        <NewSentenceDialog
          value={customText}
          onChange={setCustomText}
          onSubmit={submitCustom}
          onClose={() => setNewSentenceOpen(false)}
          submitting={submitting}
        />
      )}
    </TeacherShell>
  );
}

function AnalysisPlaceholder() {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
      Analysis in progress… this usually takes a few seconds for a new sentence.
    </div>
  );
}

function NewSentenceDialog({ value, onChange, onSubmit, onClose, submitting }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            Analyze a new sentence
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          >
            <X className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-3">
          <textarea
            autoFocus
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={4}
            className="w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Paste or type a sentence to analyze…"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.trim() || submitting}
              className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white dark:bg-white dark:text-gray-900 disabled:opacity-40"
            >
              {submitting ? 'Analyzing…' : 'Analyze'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
